<script setup>
import { railsApi, railsApiDownload } from '@/utils/railsApi'

definePage({
  meta: {
    adminArea: true,
  },
})

const { t } = useI18n()

const tab = ref('branding')
const loading = ref(false)
const saving = ref(false)
const previewing = ref(false)
const error = ref('')
const success = ref('')
const profile = ref(null)
const templates = ref([])
const selectedPurpose = ref('invoice_delivery')
const currentPassword = ref('')

const purposeOptions = computed(() => [
  { title: t('admin.billingSettings.purposes.invoice_delivery'), value: 'invoice_delivery' },
  { title: t('admin.billingSettings.purposes.payment_failed'), value: 'payment_failed' },
  { title: t('admin.billingSettings.purposes.invoice_overdue'), value: 'invoice_overdue' },
])

const activeTemplate = computed(() =>
  templates.value.find(item => item.purpose === selectedPurpose.value) || null,
)

const activeTemplatePlaceholders = computed(() =>
  (activeTemplate.value?.placeholders || []).map(p => `{{${p}}}`).join(', '),
)

const load = async () => {
  loading.value = true
  error.value = ''
  try {
    const [profileData, templatesData] = await Promise.all([
      railsApi('/api/v1/admin/billing_profile'),
      railsApi('/api/v1/admin/billing_email_templates'),
    ])

    profile.value = { ...profileData.billing_profile }
    templates.value = (templatesData.billing_email_templates || []).map(item => ({ ...item }))
  }
  catch (err) {
    error.value = err.payload?.error || err.message || t('admin.billingSettings.loadError')
  }
  finally {
    loading.value = false
  }
}

onMounted(load)

const saveProfile = async () => {
  if (!profile.value)
    return
  if (!currentPassword.value) {
    error.value = t('admin.billingSettings.currentPasswordHint')
    return
  }

  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const data = await railsApi('/api/v1/admin/billing_profile', {
      method: 'PATCH',
      body: {
        current_password: currentPassword.value,
        billing_profile: {
          legal_name: profile.value.legal_name,
          address_line1: profile.value.address_line1,
          address_line2: profile.value.address_line2,
          postal_code: profile.value.postal_code,
          city: profile.value.city,
          country: profile.value.country,
          kvk_number: profile.value.kvk_number,
          vat_number: profile.value.vat_number,
          iban: profile.value.iban,
          logo_url: profile.value.logo_url,
        },
      },
    })

    profile.value = { ...data.billing_profile }
    currentPassword.value = ''
    success.value = t('admin.billingSettings.saveSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.billingSettings.saveError')
  }
  finally {
    saving.value = false
  }
}

const previewInvoice = async () => {
  if (!profile.value)
    return

  previewing.value = true
  error.value = ''
  try {
    await railsApiDownload('/api/v1/admin/billing_profile/preview', {
      filename: 'factuur-voorbeeld.pdf',
      method: 'POST',
      body: {
        billing_profile: {
          legal_name: profile.value.legal_name,
          address_line1: profile.value.address_line1,
          address_line2: profile.value.address_line2,
          postal_code: profile.value.postal_code,
          city: profile.value.city,
          country: profile.value.country,
          kvk_number: profile.value.kvk_number,
          vat_number: profile.value.vat_number,
          iban: profile.value.iban,
          logo_url: profile.value.logo_url,
        },
      },
    })
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.billingSettings.previewError')
  }
  finally {
    previewing.value = false
  }
}

const saveTemplate = async () => {
  if (!activeTemplate.value)
    return

  saving.value = true
  error.value = ''
  success.value = ''
  try {
    const data = await railsApi(`/api/v1/admin/billing_email_templates/${activeTemplate.value.id}`, {
      method: 'PATCH',
      body: {
        billing_email_template: {
          subject: activeTemplate.value.subject,
          body: activeTemplate.value.body,
        },
      },
    })

    const idx = templates.value.findIndex(item => item.id === data.billing_email_template.id)
    if (idx >= 0)
      templates.value[idx] = { ...data.billing_email_template }
    success.value = t('admin.billingSettings.saveSuccess')
  }
  catch (err) {
    error.value = err.payload?.errors?.[0] || err.payload?.error || err.message || t('admin.billingSettings.saveError')
  }
  finally {
    saving.value = false
  }
}
</script>

<template>
  <div>
    <h1 class="text-h4 mb-4">
      {{ $t('admin.billingSettings.title') }}
    </h1>

    <VAlert
      v-if="error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ error }}
    </VAlert>
    <VAlert
      v-if="success"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ success }}
    </VAlert>

    <div
      v-if="loading"
      role="status"
    >
      {{ $t('common.loading') }}
    </div>

    <VCard
      v-else
      flat
      border
    >
      <VTabs v-model="tab">
        <VTab value="branding">
          {{ $t('admin.billingSettings.brandingTab') }}
        </VTab>
        <VTab value="email">
          {{ $t('admin.billingSettings.emailTab') }}
        </VTab>
      </VTabs>

      <VCardText>
        <VWindow v-model="tab">
          <!-- pt-1: VWindow clips outlined field floating labels (overflow:hidden) -->
          <VWindowItem
            value="branding"
            class="pt-1"
          >
            <VForm
              v-if="profile"
              @submit.prevent="saveProfile"
            >
              <VRow>
                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="profile.legal_name"
                    :label="$t('admin.billingSettings.legalName')"
                    required
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="profile.country"
                    :label="$t('admin.billingSettings.country')"
                  />
                </VCol>
                <VCol cols="12">
                  <VTextField
                    v-model="profile.address_line1"
                    :label="$t('admin.billingSettings.addressLine1')"
                    required
                  />
                </VCol>
                <VCol cols="12">
                  <VTextField
                    v-model="profile.address_line2"
                    :label="$t('admin.billingSettings.addressLine2')"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="profile.postal_code"
                    :label="$t('admin.billingSettings.postalCode')"
                    required
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="8"
                >
                  <VTextField
                    v-model="profile.city"
                    :label="$t('admin.billingSettings.city')"
                    required
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="profile.kvk_number"
                    :label="$t('admin.billingSettings.kvkNumber')"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="profile.vat_number"
                    :label="$t('admin.billingSettings.vatNumber')"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="4"
                >
                  <VTextField
                    v-model="profile.iban"
                    :label="$t('admin.billingSettings.iban')"
                  />
                </VCol>
                <VCol cols="12">
                  <VTextField
                    v-model="profile.logo_url"
                    :label="$t('admin.billingSettings.logoUrl')"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="currentPassword"
                    type="password"
                    autocomplete="current-password"
                    :label="$t('admin.billingSettings.currentPassword')"
                    :hint="$t('admin.billingSettings.currentPasswordHint')"
                    persistent-hint
                    required
                  />
                </VCol>
              </VRow>
              <div class="d-flex flex-wrap ga-2 mt-2">
                <VBtn
                  type="submit"
                  color="primary"
                  :loading="saving"
                  :disabled="!currentPassword"
                >
                  {{ $t('admin.billingSettings.saveBranding') }}
                </VBtn>
                <VBtn
                  type="button"
                  variant="outlined"
                  color="primary"
                  :loading="previewing"
                  :disabled="saving"
                  @click="previewInvoice"
                >
                  {{ $t('admin.billingSettings.previewInvoice') }}
                </VBtn>
              </div>
            </VForm>
          </VWindowItem>

          <VWindowItem
            value="email"
            class="pt-1"
          >
            <VSelect
              v-model="selectedPurpose"
              :items="purposeOptions"
              item-title="title"
              item-value="value"
              :label="$t('admin.billingSettings.emailPurpose')"
              class="mb-4"
            />

            <VForm
              v-if="activeTemplate"
              @submit.prevent="saveTemplate"
            >
              <VTextField
                v-model="activeTemplate.subject"
                :label="$t('admin.billingSettings.subject')"
                class="mb-4"
                required
              />
              <VTextarea
                v-model="activeTemplate.body"
                :label="$t('admin.billingSettings.body')"
                rows="12"
                class="mb-2"
                required
              />
              <p class="text-caption text-medium-emphasis mb-4">
                {{ $t('admin.billingSettings.placeholders') }}:
                {{ activeTemplatePlaceholders }}
              </p>
              <VBtn
                type="submit"
                color="primary"
                :loading="saving"
              >
                {{ $t('admin.billingSettings.saveTemplate') }}
              </VBtn>
            </VForm>
          </VWindowItem>
        </VWindow>
      </VCardText>
    </VCard>
  </div>
</template>
